import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Image as ImageIcon } from "lucide-react";
import {
  SpineAvatarPicker,
  type SpineAvatarPickerSelection,
  type SpineAvatarPickerPinned,
} from "../../../../components/SpineAvatarPicker";
import { TemplateStaticImagePreview } from "./TemplateStaticImagePreview";
import { toast } from "@/components/ui/use-toast";
import useSWRMutation from "swr/mutation";
import { formDataFetcher } from "api/swr";

interface PreviewPickerProps {
  value?: string;
  previewUrl?: string;
  pinnedItems?: SpineAvatarPickerPinned[];
  onSelect: (
    previewValue: string,
    previewImageUrl: string,
    videoPreview?: string,
    videoPreviewPreview?: string,
    previewId?: string,
  ) => void;
  tid: string;
}

export function PreviewPicker({
  value,
  previewUrl: initialPreviewUrl,
  pinnedItems,
  onSelect,
  tid,
}: PreviewPickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"avatar" | "upload">("avatar");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tempPreviewUrl, setTempPreviewUrl] = useState<string>("");
  const [tempVideoPreviewUrl, setTempVideoPreviewUrl] = useState<string>("");
  const [tempVideoPreviewPreviewUrl, setTempVideoPreviewPreviewUrl] =
    useState<string>("");
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const { trigger: uploadImage } = useSWRMutation(
    `/spine-templates/upload-preview`,
    formDataFetcher
  );

  const handleAvatarSelect = (avatar: SpineAvatarPickerSelection) => {
    if (!avatar.url) return;
    setSelectedAvatarId(avatar.id);
    setTempPreviewUrl(avatar.url);
    setTempVideoPreviewUrl(avatar.videoUrl || "");
    setTempVideoPreviewPreviewUrl(avatar.videoPreviewUrl || "");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast({
          variant: "destructive",
          title: "文件类型错误",
          description: "请选择图片文件",
        });
        return;
      }
      setSelectedFile(file);
      // 创建本地预览
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleConfirm = async () => {
    // 如果是上传模式且有选择的文件，先上传
    if (selectedTab === "upload" && selectedFile) {
      try {
        setUploading(true);

        const formData = new FormData();
        formData.append("preview", selectedFile);
        formData.append("tid", tid);

        const result = await uploadImage(formData);

        if (result.url) {
          toast({
            title: "上传成功",
            description: "预览图片已上传",
          });
          // 上传模式：显示 URL，预览也用 URL；上传图不对应 avatar，清空 previewId
          onSelect(result.url, result.url, "", "", "");
          setOpen(false);
          resetState();
        }
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "上传失败",
          description: error.message || "未知错误",
        });
      } finally {
        setUploading(false);
      }
      return;
    }

    // Avatar 模式：与上传一致，模版 preview 存图片 URL（列表/发布里用 cdnUrl(preview)）
    // 注意：置顶的「当前 Preview」没有 avatar id，只要有 url 即可选中
    if (!tempPreviewUrl) {
      toast({
        variant: "destructive",
        title: "请选择预览图",
        description: "请选择一个有效的 Avatar",
      });
      return;
    }

    onSelect(
      tempPreviewUrl,
      tempPreviewUrl,
      tempVideoPreviewUrl || "",
      tempVideoPreviewPreviewUrl || "",
      selectedAvatarId || "",
    );
    setOpen(false);
    resetState();
  };

  const handleCancel = () => {
    setOpen(false);
    resetState();
  };

  const resetState = () => {
    setSelectedFile(null);
    setTempPreviewUrl("");
    setTempVideoPreviewUrl("");
    setTempVideoPreviewPreviewUrl("");
    setSelectedAvatarId("");
    setSelectedTab("avatar");
  };

  return (
    <div className="space-y-2">
      <Label>Preview</Label>
      <div className="flex gap-2">
        <div className="flex-1">
          <input
            type="text"
            value={value || ""}
            readOnly
            className="w-full px-3 py-2 border rounded-md bg-muted/50 text-sm"
          />
        </div>
        <Button onClick={() => setOpen(true)} variant="outline">
          选择 Preview
        </Button>
      </div>

      {initialPreviewUrl && (
        <TemplateStaticImagePreview
          label="Preview 预览"
          imageUrl={initialPreviewUrl}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>选择 Preview</DialogTitle>
          </DialogHeader>

          <Tabs
            value={selectedTab}
            onValueChange={(v) => setSelectedTab(v as "avatar" | "upload")}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="avatar" className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                选择 Avatar
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                上传图片
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="avatar"
              className="flex-1 overflow-auto mt-4 space-y-4"
            >
              <div className="text-sm text-muted-foreground mb-4">
                从已上传的 Avatar 中选择一个作为 Preview（需要有可用的 URL）
              </div>
              <SpineAvatarPicker
                tid={tid}
                currentAvatarId={undefined}
                pinnedItems={pinnedItems}
                onSelect={handleAvatarSelect}
              >
                <Button variant="outline" className="w-full">
                  选择 Avatar
                </Button>
              </SpineAvatarPicker>

              {tempPreviewUrl && selectedTab === "avatar" && (
                <div className="mt-4 space-y-2">
                  <TemplateStaticImagePreview
                    label="已选择的 Preview"
                    imageUrl={tempPreviewUrl}
                    sizeClassName="max-h-64 max-w-sm h-auto w-auto min-h-[8rem] min-w-[8rem]"
                  />
                  <div className="text-xs text-muted-foreground break-all">
                    Avatar ID: {selectedAvatarId}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent
              value="upload"
              className="flex-1 overflow-auto mt-4 space-y-4"
            >
              <div className="text-sm text-muted-foreground mb-4">
                直接上传图片文件作为 Preview
              </div>

              <div className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="preview-upload"
                  />
                  <label htmlFor="preview-upload" className="cursor-pointer">
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground">
                      {selectedFile
                        ? `已选择: ${selectedFile.name}`
                        : "点击选择图片文件"}
                    </p>
                  </label>
                </div>

                {tempPreviewUrl && selectedTab === "upload" && (
                  <div className="mt-4">
                    <Label>预览</Label>
                    <div className="mt-2 flex justify-center">
                      <img
                        src={tempPreviewUrl}
                        alt="Upload Preview"
                        className="max-w-sm max-h-64 object-contain rounded-md border"
                      />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={uploading}>
              取消
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={(selectedTab === "avatar" && !tempPreviewUrl) || uploading}
            >
              {uploading ? "上传中..." : "确定"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
